import React, { useEffect, useState, useMemo } from "react";
import { collection, orderBy, query, onSnapshot } from "firebase/firestore";
import { db, app } from "../utils/init-firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  Flex,
  useColorModeValue,
  Avatar,
  useDisclosure,
  useToast,
  Tabs,
  TabList,
  TabPanel,
  Tab,
  TabPanels,
  Box,
  Text,
  VStack,
  Wrap,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Select,
  Badge,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import DeleteDialog from "../components/Dialogs/DeleteDialog";
import { useHistory } from "react-router-dom";
import { useAppSettings } from "../hooks/useAppSettings";
import AppAccessPanel from "../components/Admin/AppAccessPanel";
import UserAdminControls from "../components/Admin/UserAdminControls";

// Satellite apps managed from the Apps tab. Each entry seeds the Firestore
// appSettings doc on first initialize; already-initialized apps read from
// Firestore, and any extra appSettings docs render too.
const KNOWN_APPS = [
  {
    id: "forecast",
    initConfig: {
      appName: "2026 Forecast Dashboard",
      repoOwner: "rob-futureloans",
      repoName: "Future-Home-Loans-2026-Forecast-Dashboard-BOLT",
      workflowFile: "deploy.yml",
      deployBranch: "main",
      firebaseSiteId: "bolt-forecast-1",
      appUrl: "https://forecast.fhl.one",
    },
  },
  {
    id: "launchpad",
    initConfig: {
      appName: "LaunchPad",
      repoOwner: "rob-futureloans",
      repoName: "Launchpad",
      workflowFile: "deploy.yml",
      deployBranch: "main",
      firebaseSiteId: "bolt-launchpad-1",
      appUrl: "https://launchpad.fhl.one",
    },
  },
];

export default function AdminSettingsPage() {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchOptions, setBranchOptions] = useState([]);
  const [activeUid, setActiveUid] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const history = useHistory();
  const {
    appSettings,
    toggleUserAccess,
    toggleAllowAll,
    triggerDeploy,
    fetchDeployHistory,
    rollbackDeploy,
    initializeApp: initializeAppSetting,
  } = useAppSettings();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedUser, setSelectedUser] = useState(null);
  const cancelRef = React.useRef();
  const toast = useToast();

  const listBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const activeBg = useColorModeValue("blue.50", "blue.900");
  const activeBorder = useColorModeValue("blue.400", "blue.300");
  const subtleColor = useColorModeValue("gray.500", "gray.400");
  const searchBarBg = useColorModeValue("gray.50", "gray.900");

  const handleOnClose = () => {
    setSelectedUser(null);
    onClose();
  };

  const handleOnOpen = (uid, name) => {
    setSelectedUser({ uid: uid, name: name });
    onOpen();
  };

  const handleDeleteUser = () => {
    deleteUser();
    handleOnClose();
  };

  const deleteUser = async () => {
    try {
      const functionsObj = getFunctions(app, "us-east1");
      const deactivateUser = httpsCallable(functionsObj, "deactivateUser");
      await deactivateUser({ uid: selectedUser.uid });
      toast({
        title: "User successfully deactivated!",
        description: "Auth account disabled, sessions revoked, and profile deleted.",
        status: "success",
        position: "top",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      const message = err?.message || String(err);
      toast({
        title: "Error deactivating user",
        description: message,
        status: "error",
        position: "top",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, "users"), orderBy("dateJoined", "desc"));
    const unsub = onSnapshot(q, (querySnapshot) => {
      const users = [];
      const branches = [];
      const seenBranches = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        users.push({ ...data });
        if (data.Branch && !seenBranches.includes(data.Branch)) {
          branches.push({ label: data.Branch, value: data.Branch });
          seenBranches.push(data.Branch);
        }
      });
      setBranchOptions(branches);
      setAllUsers(users);
      setLoading(false);
    });

    return () => {
      unsub();
    };
  }, []);

  // Auto-select the first user once the list has loaded.
  useEffect(() => {
    if (!activeUid && allUsers.length > 0) {
      setActiveUid(allUsers[0].uid);
    }
  }, [allUsers, activeUid]);

  const roleOptions = useMemo(() => {
    const set = new Set();
    allUsers.forEach((u) => {
      (Array.isArray(u.Roles) ? u.Roles : []).forEach((r) => r && set.add(r));
    });
    return Array.from(set).sort();
  }, [allUsers]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allUsers.filter((u) => {
      const matchesSearch =
        !q ||
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q);
      const matchesRole =
        !roleFilter || (Array.isArray(u.Roles) && u.Roles.includes(roleFilter));
      return matchesSearch && matchesRole;
    });
  }, [allUsers, search, roleFilter]);

  const activeUser = useMemo(
    () => allUsers.find((u) => u.uid === activeUid) || null,
    [allUsers, activeUid]
  );

  const navigateToChatbotAnalytics = () => {
    history.push("/admin/chatbot-analytics");
  };

  return (
    <>
      {!loading && (
        <>
          <Flex justifyContent="flex-end" mb={4} mt={2}>
            <Button onClick={navigateToChatbotAnalytics} colorScheme="cyan">
              Chatbot Analytics
            </Button>
          </Flex>
          <Tabs mt="5">
            <TabList>
              <Tab>Users</Tab>
              <Tab>Apps</Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={0}>
                <Flex direction={{ base: "column", md: "row" }} gap={4} align="stretch">
                  {/* Master: user list */}
                  <Box
                    w={{ base: "100%", md: "340px" }}
                    flexShrink={0}
                    bg={listBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="lg"
                    overflow="hidden"
                    alignSelf="flex-start"
                  >
                    <Box bg={searchBarBg} p={3} borderBottomWidth="1px" borderColor={borderColor}>
                      <InputGroup size="sm" mb={2}>
                        <InputLeftElement pointerEvents="none">
                          <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                          placeholder="Search by name or email..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          bg={listBg}
                        />
                      </InputGroup>
                      <Select
                        size="sm"
                        bg={listBg}
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        placeholder="All roles"
                      >
                        {roleOptions.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </Select>
                      <Text fontSize="xs" color={subtleColor} mt={2}>
                        {filteredUsers.length} of {allUsers.length} users
                      </Text>
                    </Box>

                    <VStack
                      spacing={0}
                      align="stretch"
                      maxH="70vh"
                      overflowY="auto"
                    >
                      {filteredUsers.map((user) => {
                        const isActive = user.uid === activeUid;
                        return (
                          <Flex
                            key={user.uid}
                            align="center"
                            gap={3}
                            p={3}
                            cursor="pointer"
                            bg={isActive ? activeBg : "transparent"}
                            borderLeftWidth="3px"
                            borderLeftColor={isActive ? activeBorder : "transparent"}
                            _hover={{ bg: isActive ? activeBg : hoverBg }}
                            onClick={() => setActiveUid(user.uid)}
                          >
                            <Avatar size="sm" name={user.name} src={user.photo} />
                            <Box flex="1" minW={0}>
                              <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                                {user.name || "No Name"}
                              </Text>
                              <Text fontSize="xs" color={subtleColor} noOfLines={1}>
                                {user.email}
                              </Text>
                              <Wrap spacing={1} mt={1} shouldWrapChildren>
                                {user.SuperAdmin && (
                                  <Badge colorScheme="red" fontSize="0.6rem">
                                    Super
                                  </Badge>
                                )}
                                {user.CRMUser && (
                                  <Badge colorScheme="purple" fontSize="0.6rem">
                                    CRM
                                  </Badge>
                                )}
                                {user.isEOSUser && (
                                  <Badge colorScheme="green" fontSize="0.6rem">
                                    EOS
                                  </Badge>
                                )}
                              </Wrap>
                            </Box>
                          </Flex>
                        );
                      })}
                      {filteredUsers.length === 0 && (
                        <Text color={subtleColor} textAlign="center" py={6} fontSize="sm">
                          No users found.
                        </Text>
                      )}
                    </VStack>
                  </Box>

                  {/* Detail: settings for the selected user */}
                  <Box flex="1" minW={0}>
                    {activeUser ? (
                      <UserAdminControls
                        user={activeUser}
                        branchOptions={branchOptions}
                        allowDeactivate
                        onRequestDeactivate={handleOnOpen}
                        onOpenProfile={(uid) => history.push(`/profile/${uid}`)}
                      />
                    ) : (
                      <Flex
                        h="200px"
                        align="center"
                        justify="center"
                        borderWidth="1px"
                        borderStyle="dashed"
                        borderColor={borderColor}
                        borderRadius="lg"
                      >
                        <Text color={subtleColor}>Select a user to manage their settings.</Text>
                      </Flex>
                    )}
                  </Box>
                </Flex>
              </TabPanel>
              <TabPanel>
                <Box mt={4}>
                  <Text fontSize="xl" fontWeight="bold" mb={4}>
                    App Access Management
                  </Text>
                  <Text fontSize="sm" color="gray.500" mb={6}>
                    Control which users can access secondary applications. Changes take effect on
                    their next login or within 1 hour.
                  </Text>
                  <VStack spacing={8} align="stretch">
                    {[
                      ...KNOWN_APPS,
                      ...appSettings
                        .filter((a) => !KNOWN_APPS.some((k) => k.id === a.id))
                        .map((a) => ({ id: a.id, initConfig: null })),
                    ].map(({ id, initConfig }) => (
                      <AppAccessPanel
                        key={id}
                        appId={id}
                        initConfig={initConfig}
                        appConfig={appSettings.find((a) => a.id === id) || null}
                        allUsers={allUsers}
                        onToggleAllowAll={toggleAllowAll}
                        onToggleUserAccess={toggleUserAccess}
                        onTriggerDeploy={triggerDeploy}
                        onFetchDeployHistory={fetchDeployHistory}
                        onRollbackDeploy={rollbackDeploy}
                        onInitialize={initializeAppSetting}
                        isSuperAdmin={true}
                      />
                    ))}
                  </VStack>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>

          <DeleteDialog
            handleDelete={handleDeleteUser}
            item="User"
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={handleOnClose}
            cancelRef={cancelRef}
            selectedUser={selectedUser}
          />
        </>
      )}
    </>
  );
}
